/** @jsxImportSource @emotion/react */
import React, { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { Link, navigate } from '@reach/router';
import instance, { isErrorHandled, useFetch } from 'api';
import Button from 'components/button/Button';
import { endpoints } from 'endpoints.config';
import { Form, Formik, FormikHelpers, FormikProps } from 'formik';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { getCustomerComponentResources, selectUserInfo } from 'reducers/componentResources';
import { TaxIDCollection } from 'components/TaxIDCollection/TaxIDCollection';
import { BrandVisual } from 'components/brandVisual/BrandVisual';
import { css, useTheme } from '@emotion/react';
import { Toast, ToastMessageReason } from 'helpers/toast';
import { getErrorMessage } from 'errors';
import { toCamelCase } from 'helpers/formatFormFieldNames';
import { Spinner } from 'components/spinner/Spinner';
import { FormBuilder } from 'components/FormBuilder';
import { ApiResponse } from 'models/formBuilder';
import { ModalTypes, openModal, PendingCard } from 'reducers/modal';
import { FormFieldRendererOverride } from '../../components/categoryComponents/Entity/Entity';
import { ShippingMethod } from './formRenderers/ShippingMethod';
import ESignModal from 'components/e-sign/ESignModal';
import { ThreeBounce } from 'better-react-spinkit';
import { cloneDeep } from 'lodash';
import FormElements from 'components/categoryHelpers/FormElements';
import { dummy_data_business_customer } from './dummy_data_business_customer';
import { dummy_data_personal_customer } from './dummy_data_personal_customer';
import UploadField from 'components/FormBuilder/UploadField';
import { useSelector } from 'react-redux';
import { Modal as ModalComponent } from '../../components/modal/Modal';
import {
    mapCardCreateRequiredFieldErrors,
    HolderFieldMeta,
} from './cardCreateHolderFieldErrors';
type Step = 'tax-id-collection' |'render-form' | 'doc-upload' | 'sign-consent';

interface FieldMetadata {
    name: string;
    type: string;
    label?: string;
    bRequired?: boolean;
}

type FormData = {
    businessActivityType?: string;
    complianceFieldOptions: any;
    currency?: string;
    customerSubtype?: string;
    occupationType?: string;
    sourceOfFundsType?: string;
    taxNumber?: string;
    dateOfBirth?: string | null;
    firstName?: string;
    lastName?: string;
    phoneNumber?: string;
    emailAddress?: string;
    legalEntityName?: string;
    dateOfIncorporation?: string | null;
    registeredAddressStreet?: string;
    registeredAddressNumber?: string;
    registeredAddressCity?: string;
    registeredAddressState?: string;
    registeredAddressPostcode?: string;
    registeredAddressCountry?: string;
    shippingAddressLine1?: string;
    shippingAddressLine2?: string;
    shippingAddressCity?: string;
    shippingAddressState?: string;
    shippingAddressPostcode?: string;
    shippingAddressCountry?: string;
    shippingMethod: string;
    ownershipStructureChanged?: boolean;
    bDocUploaded?: boolean;
    product?: string;
    cardType?: string;
    selectedDDAAccountId?: number | null;
};
interface DDAAccount {
        id: number;
        accountName: string;
        accountNumber: string;
        routingNumber: string;
};
const overrideFormFieldRenderer: FormFieldRendererOverride<FormData> = {
    shippingMethod: (_key, field) => <ShippingMethod field={field} />,
};
export const AddCard = () => {
    const theme = useTheme();
    const userInfo = useSelector(selectUserInfo);

    const [showDDAModal, setShowDDAModal] = useState(false);
    const [ddaAccounts, setDDAAccounts] = useState<DDAAccount[]>([]);
    const [selectedDDAAccountId, setSelectedDDAAccountId] = useState<number | null>(null);
    const [pendingFormValues, setPendingFormValues] = useState<FormData | null>(null);
    const [formValues, setFormValues] = useState<FormData | null>(null);
    const [retryValues, setRetryValues] = useState<FormData | null>(null);
    const [injectedHolderFields, setInjectedHolderFields] = useState<HolderFieldMeta[]>([]);
    const [submitingRequest, setSubmitingRequest] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [step, setStep] = useState<Step>('render-form');
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
    const [formValidationErrors, setFormValidationErrors] = useState<
        { type: string; fieldName: string; messageText: string }[]
    >([]);
    const [isSameAsRegisteredAddress, setIsSameAsRegisteredAddress] = useState(true); // defaultChecked

    const handleToggle = (e: ChangeEvent<HTMLInputElement>): void => {
        const isChecked = e.target.checked;
        setIsSameAsRegisteredAddress(isChecked);
    };

    const addAccountCss = css({ h3: { color: theme.colors.second } });
    const dispatch = useDispatch();

    const { t } = useTranslation('common');

    const { data, loading } = useFetch<ApiResponse<FormData>>(endpoints.cards.reviewcreatecard);
    const [showTaxIdModal, setShowTaxIdModal] = useState(false);
        useEffect(() => {
            if (userInfo?.topUpCardProduct === 'C' && userInfo?.bRequireTaxID === true) {
                console.log('[AddCard] Tax ID required - showing modal');
                setShowTaxIdModal(true);
            }
        }, [userInfo]);
        const handleTaxIdUpdate = async (taxId: string): Promise<boolean> => {
        try {
            const response = await instance.post<ApiResponse<any>>(
                endpoints.profilemodule.updateTaxID,
                { taxId }
            );

            if (response.data.status === '1') {
                Toast.openSuccessToast('Tax ID saved successfully');
                return true;
            } else {
                Toast.openErrorToast('Failed to update Tax ID');
                return false;
            }
        } catch (error: any) {
            console.error('[AddCard TaxID] Error:', error);
            Toast.openErrorToast('Failed to update Tax ID');
            return false;
        }
    };
    const handleSubmit = useCallback((values: FormData, formikHelpers: FormikHelpers<FormData>) => {
        setErrorMessage('');
        formikHelpers.setSubmitting(false);

        setFormValues(values);

        if (values.bDocUploaded) {
            setStep('sign-consent');
        } else {
            setStep('doc-upload');
        }
    }, []);

    const handleDocUploadSubmit = async (
        values: { Type: string; DocumentSide: string; file: File | null },
        actions: FormikHelpers<{ Type: string; DocumentSide: string; file: File | null }>,
        setStep: (step: Step) => void
    ) => {
        if (!values.file) {
            Toast.openToastMessage(
                'Please upload the document before continuing',
                ToastMessageReason.ERROR
            );
            return;
        }

        const formData = new FormData();
        formData.append('Type', values.Type);
        formData.append('DocumentSide', values.DocumentSide);
        formData.append('File', values.file);

        try {
            const res = await instance.post(endpoints.cards.documentupload, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            // if (res.status === 200 && res.data?.status === '1') {
            setStep('sign-consent');
            // } else {
            //     Toast.openToastMessage(
            //         'Document upload failed. Please try again.',
            //         ToastMessageReason.ERROR
            //     );
            // }
        } catch (err) {
            Toast.openToastMessage(
                'An error occurred while uploading. Please try again.',
                ToastMessageReason.ERROR
            );
        }
    };
    const submitAddCard = useCallback(async (values: FormData) => {
    setSubmitingRequest(true);
        const valuesToPost = cloneDeep(values);
        valuesToPost.cardType = values.cardType === '1' ? 'Physical' : 'Virtual';
        setFormValues(null);
        setStep('render-form');

        try {
            const res = await instance.post<ApiResponse<{ cardUrlPath: string }>>(
                endpoints.cards.addCard,
                {
                    ...valuesToPost,
                    BusinessRegistrationNumber: '123',
                    PubliclyListed: false,
                    LicenseRequired: false,
                }
            );

            setSubmitingRequest(false);

            if (res?.data?.details?.bReadyForActivation) {
                dispatch(getCustomerComponentResources());
                return navigate(`../${res.data.details.cardUrlPath}`);
            } else {
                const newPendingCard: PendingCard = {
                    id: res?.data?.details?.topUpCardsId,
                    cardUrlPath: res?.data?.details?.cardUrlPath,
                    bReadyForActivation: res?.data?.details?.bReadyForActivation,
                };
                dispatch(openModal({ modalType: ModalTypes.PENDING_CARD, data: newPendingCard }));
            }
        } catch (err: any) {
            setSubmitingRequest(false);
            setStep('render-form');

            if (isErrorHandled(err)) {
                const errors = err.response.data.errors as { fieldName: string; messageCode: string }[];
                const ddaError = errors.find(
                    (e) => toCamelCase(e.fieldName) === 'dDAAccount' && e.messageCode === 'SELECT_DDA_FOR_BUSINESS_USER'
                );

                if (ddaError) {
                    try {
                        const ddaRes = await instance.get<ApiResponse<DDAAccount[]>>(
                            endpoints.cards.businessDdaAccounts
                        );
                        if (ddaRes.data.status === '1' && ddaRes.data.details?.length) {
                            setDDAAccounts(ddaRes.data.details);
                            setPendingFormValues(values);
                            setShowDDAModal(true);
                        } else {
                            Toast.openGenericErrorToast();
                        }
                    } catch {
                        Toast.openGenericErrorToast();
                    }
                    return;
                }

                // Holder / address Required FieldKeys → show placeholders + keep values for retry
                const holderMapped = mapCardCreateRequiredFieldErrors(errors);
                if (holderMapped.length > 0) {
                    setRetryValues(values);
                    setInjectedHolderFields(holderMapped.map((m) => m.meta));
                    const holderErrors: Record<string, string> = {};
                    const validationErrors: { type: string; fieldName: string; messageText: string }[] = [];
                    holderMapped.forEach((m) => {
                        holderErrors[m.formFieldName] = m.message;
                        validationErrors.push({
                            type: 'BadRequest',
                            fieldName: m.meta.name,
                            messageText: m.message,
                        });
                    });
                    setFieldErrors(holderErrors);
                    setFormValidationErrors(validationErrors);
                    Toast.openToastMessage(
                        'Please complete the required fields highlighted below',
                        ToastMessageReason.ERROR
                    );
                    return;
                }

                const newFieldErrors: Record<string, string> = {};
                let errorToast = false;
                errors.forEach((error) => {
                    const fieldName = toCamelCase(error.fieldName);
                    newFieldErrors[fieldName] = error.messageCode;
                    if (!errorToast) {
                        errorToast = true;
                        Toast.openToastMessage(error.messageCode, ToastMessageReason.ERROR);
                    }
                });
                setFieldErrors(newFieldErrors);
            } else {
                Toast.openGenericErrorToast();
            }
        }
    }, [dispatch]);
    const handleConcentAccepted = useCallback(() => {
        if (formValues?.ownershipStructureChanged) {
            setFormValues(null);
            setStep('render-form');
            dispatch(openModal({ modalType: ModalTypes.CONTACT_SUPPORT, data: {} }));
        } else {
            submitAddCard(formValues ?? data?.details);
        }
    }, [dispatch, formValues, data, submitAddCard]);

    const handleDDAConfirm = useCallback(() => {
        if (!selectedDDAAccountId || !pendingFormValues) return;
        setShowDDAModal(false);
        submitAddCard({ ...pendingFormValues, selectedDDAAccountId });
        setSelectedDDAAccountId(null);
        setPendingFormValues(null);
    }, [selectedDDAAccountId, pendingFormValues, submitAddCard]);

    const initialValues: FormData = useMemo(() => {
        const camelCasedDetails = Object.keys(data?.details || {}).reduce((acc, key) => {
            acc[toCamelCase(key)] = data?.details[key];
            return acc;
        }, {} as Record<string, any>);

        const metadata = data?.metadata as FieldMetadata[];
        // Define the new toggle field
        const sameAsField: FieldMetadata = {
            name: 'sameAsRegisteredAddress',
            type: 'Boolean',
            label: 'Same As Registered Address',
        };

        // Build the extended metadata, inserting before ShippingAddressLine1
        const extendedMetadata: FieldMetadata[] = [];

        (metadata || []).forEach((field) => {
            if (field.name === 'ShippingAddressLine1') {
                extendedMetadata.push(sameAsField);
            }
            extendedMetadata.push(field);
        });

        // Inject holder placeholders returned as Required from Validate (tax, DOB, etc.)
        const existingNames = new Set(
            extendedMetadata.map((f) => f.name.toLowerCase())
        );
        injectedHolderFields.forEach((field) => {
            if (!existingNames.has(field.name.toLowerCase())) {
                extendedMetadata.push({
                    name: field.name,
                    type: field.type,
                    label: field.label,
                    bRequired: field.bRequired ?? true,
                });
                existingNames.add(field.name.toLowerCase());
            }
        });

        const mappedFields = extendedMetadata.reduce((prev, field) => {
            const key = toCamelCase(field.name);
            switch (field.type.toLowerCase()) {
                case 'string':
                case 'text':
                    prev[key] = '';
                    break;
                case 'number':
                case 'integer':
                    prev[key] = null;
                    break;
                case 'boolean':
                    prev[key] = false;
                    break;
                case 'multiselect':
                    prev[key] = [];
                    break;
                default:
                    prev[key] = null;
            }
            return prev;
        }, {} as Record<string, any>);

        return {
            complianceFieldOptions: {},
            shippingMethod: '',
            product: data?.details?.product || '',
            ...mappedFields,
            ...camelCasedDetails,
            ...(retryValues || {}),
            cardType: retryValues?.cardType ?? '1',
        } as FormData;
    }, [data, injectedHolderFields, retryValues]);

    const formMetadata = useMemo(() => {
        const metadata = [...(data?.metadata || [])] as FieldMetadata[];
        const existingNames = new Set(metadata.map((f) => f.name.toLowerCase()));
        injectedHolderFields.forEach((field) => {
            if (!existingNames.has(field.name.toLowerCase())) {
                metadata.push({
                    name: field.name,
                    type: field.type,
                    label: field.label,
                    bRequired: field.bRequired ?? true,
                });
                existingNames.add(field.name.toLowerCase());
            }
        });
        return metadata;
    }, [data?.metadata, injectedHolderFields]);

    return (
        <div className="AddAccountPage" css={addAccountCss}>
            <div className="LeftSide">
                <h3>Add New Card</h3>
                <p>You can order a prepayment card to make transactions online and in store for goods and services. You can top this card up from your other accounts.</p>
                {loading ? (
                    <Spinner />
                ) : (
                    <>
                        {step === 'render-form' &&
                            data && !data.details.bNewCustomer &&
                            (() => {
                                const product = data?.details?.product;
                                const mergedInitialValues: FormData = {
                                    ...initialValues,
                                    product,
                                };

                                return (
                                    <Formik<FormData>
                                        key={`add-card-${injectedHolderFields.map((f) => f.name).join('-') || 'base'}`}
                                        initialValues={mergedInitialValues}
                                        initialErrors={fieldErrors}
                                        enableReinitialize={true}
                                        onSubmit={handleSubmit}
                                    >
                                        {(formik: FormikProps<FormData>) => (
                                            <Form className="FormBuilderForm row">
                                                <div
                                                    className="FormBox"
                                                    style={{
                                                        width: '100%',
                                                        marginBottom: '20px',
                                                        fontFamily: '"Arial", sans-serif',
                                                    }}
                                                >
                                                    <div
                                                        className="FormBox"
                                                        style={{
                                                            width: '100%',
                                                            marginBottom: '20px',
                                                            fontFamily: '"Arial", sans-serif',
                                                        }}
                                                    >
                                                        <label
                                                            htmlFor="cardType"
                                                            style={{
                                                                display: 'block',
                                                                fontWeight: 500,
                                                                color: '#202122ff',
                                                                fontSize: '12px',
                                                                marginBottom: '6px',
                                                                fontFamily: '"Arial", serif',
                                                            }}
                                                        >
                                                            Card Type
                                                        </label>
                                                        <div
                                                            style={{
                                                                display: 'flex',
                                                                gap: '10px',
                                                                width: '100%',
                                                            }}
                                                        >
                                                            {[
                                                                {
                                                                    label: 'Physical',
                                                                    value: '1',
                                                                },
                                                                {
                                                                    label: 'Virtual',
                                                                    value: '2',
                                                                },
                                                            ].map((option) => (
                                                                <label
                                                                    key={option.value}
                                                                    style={{
                                                                        flex: 1,
                                                                        textAlign: 'center',
                                                                        padding: '10px 12px',
                                                                        borderRadius: 4,
                                                                        border:
                                                                            formik.values
                                                                                .cardType ===
                                                                            option.value
                                                                                ? '2px solid #0052cc'
                                                                                : '1px solid #D1D5DB',
                                                                        backgroundColor:
                                                                            formik.values
                                                                                .cardType ===
                                                                            option.value
                                                                                ? '#0052cc20'
                                                                                : '#f2f2f3',
                                                                        fontSize: '12px',
                                                                        color: '#323232ff',
                                                                        cursor: 'pointer',
                                                                        transition: 'all 0.2s ease',
                                                                        fontWeight:
                                                                            formik.values
                                                                                .cardType ===
                                                                            option.value
                                                                                ? '600'
                                                                                : '400',
                                                                    }}
                                                                >
                                                                    <input
                                                                        type="radio"
                                                                        name="cardType"
                                                                        value={option.value}
                                                                        checked={
                                                                            formik.values
                                                                                .cardType ===
                                                                            option.value
                                                                        }
                                                                        onChange={
                                                                            formik.handleChange
                                                                        }
                                                                        style={{
                                                                            display: 'none',
                                                                        }}
                                                                    />
                                                                    {option.label}
                                                                </label>
                                                            ))}
                                                        </div>
                                                          
                                                    </div>
                                                </div>
                                                {/* Hide shipping fields if Virtual */}
                                                <FormElements
                                                    values={formik.values}
                                                    data={{
                                                        ...data!,
                                                        metadata:
                                                            (formik.values.cardType === '2'
                                                                ? formMetadata.filter(
                                                                      (f) =>
                                                                          !f.name
                                                                              .toLowerCase()
                                                                              .includes(
                                                                                  'shipping'
                                                                              ) &&
                                                                          !f.name
                                                                              .toLowerCase()
                                                                              .includes(
                                                                                  'sameasregisteredaddress'
                                                                              )
                                                                  )
                                                                : formMetadata) as any,
                                                    }}
                                                    formValidationErrors={formValidationErrors as any}
                                                    loadingFallback={<Spinner positionAbsolute />}
                                                    overrideFormFieldRenderer={
                                                        overrideFormFieldRenderer
                                                    }
                                                />
                                                {/* {errorMessage && <div className="ErrorText">{errorMessage}</div>} */}
                                                <Button
                                                    variety="full"
                                                    type="submit"
                                                    disabled={
                                                        formik.isSubmitting || submitingRequest
                                                    }
                                                >
                                                    <span className="notranslate" translate="no">{t('continue')}</span>
                                                </Button>
                                                <p
                                                    style={{
                                                        color: 'red',
                                                        fontSize: 12,
                                                        minHeight: 20,
                                                    }}
                                                >
                                                    {errorMessage && errorMessage}
                                                </p>
                                                {submitingRequest && (
                                                    <div
                                                        style={{
                                                            display: 'flex',
                                                            justifyContent: 'center',
                                                            alignItems: 'center',
                                                            width: '100%',
                                                        }}
                                                    >
                                                        <ThreeBounce
                                                            size={20}
                                                            color={theme.colors.first}
                                                        />
                                                    </div>
                                                )}
                                            </Form>
                                        )}
                                    </Formik>
                                );
                            })()}

                        {step === 'doc-upload' && (
                            <Formik<{ Type: string; DocumentSide: string; file: File | null }>
                                initialValues={{
                                    Type: 'Tax_ID',
                                    DocumentSide: 'Front',
                                    file: null,
                                }}
                                onSubmit={(values, actions) =>
                                    handleDocUploadSubmit(values, actions, setStep)
                                }
                            >
                                {(formik) => (
                                    <Form className="FormBuilderForm row">
                                        <div className={`FormBox`}>
                                            <div className="FormLabel">
                                                <label>
                                                    {userInfo?.accountType === 'Business'
                                                        ? 'Please provide a Corporate Authorization Document (Resolution or equivalent showing executive approval of the select service/prooduct and authorized signatories)'
                                                        : 'Tax Document'}
                                                </label>
                                            </div>

                                            <UploadField
                                                fieldName={'file'}
                                                displayError={true}
                                                multi={false}
                                            />
                                        </div>
                                        <Button type="submit" variety="full">
                                            Continue
                                        </Button>
                                    </Form>
                                )}
                            </Formik>
                        )}
                    </>
                )}
            </div>

            <BrandVisual />

            {step === 'sign-consent' && (
                <ESignModal
                    onConcentAccepted={handleConcentAccepted}
                    close={() => {
                        setFormValues(null);
                        setStep('render-form');
                    }}
                    address={data?.details?.registeredAddressCountry}
                    product={data?.details?.product}
                />
            )}

            {showTaxIdModal && (
                <ModalComponent
                    title="Tax Identification Required"
                    customCloseButton={
                        <span 
                            className="CloseButton" 
                            onClick={() => {
                                setShowTaxIdModal(false);
                                navigate('/'); // Go back to home
                            }}
                            style={{ cursor: 'pointer' }}
                        >
                            ❌
                        </span>
                    }
                    className="TaxIdModal"
                    onCloseModal={() => {           // ADD THIS
                    setShowTaxIdModal(false);
                    navigate('/');
                }}
                >
                    <TaxIDCollection
                        onComplete={() => {
                            console.log('[AddCard] Tax ID complete');
                            setShowTaxIdModal(false);
                            // Refresh userInfo to update bRequireTaxID
                            dispatch(getCustomerComponentResources());
                        }}
                        onUpdateTaxID={handleTaxIdUpdate}
                    />
                </ModalComponent>
            )}
            {showDDAModal && (
                <ModalComponent
                    title="Select Account for Card"
                    onCloseModal={() => {
                        setShowDDAModal(false);
                        setSelectedDDAAccountId(null);
                        setPendingFormValues(null);
                    }}
                >
                    <div style={{ padding: '8px 0' }}>
                        <p style={{ fontSize: 13, color: '#555', marginBottom: 16 }}>
                            Multiple accounts found. Please select the account to create this card under.
                        </p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                            {ddaAccounts.map((account) => (
                                <label
                                    key={account.id}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 12,
                                        padding: '12px 14px',
                                        borderRadius: 6,
                                        border: selectedDDAAccountId === account.id
                                            ? '2px solid #0052cc'
                                            : '1px solid #D1D5DB',
                                        backgroundColor: selectedDDAAccountId === account.id
                                            ? '#0052cc10'
                                            : '#fafafa',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease',
                                    }}
                                >
                                    <input
                                        type="radio"
                                        name="ddaAccount"
                                        value={account.id}
                                        checked={selectedDDAAccountId === account.id}
                                        onChange={() => setSelectedDDAAccountId(account.id)}
                                        style={{ display: 'none' }}
                                    />
                                    <div>
                                        <div style={{ fontWeight: 600, fontSize: 13, color: '#202122' }}>
                                            {account.accountName}
                                        </div>
                                        <div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>
                                            Account: {account.accountNumber}
                                            {account.routingNumber && ` · Routing: ${account.routingNumber}`}
                                        </div>
                                    </div>
                                </label>
                            ))}
                        </div>
                        <Button
                            variety="full"
                            disabled={!selectedDDAAccountId}
                            onClick={handleDDAConfirm}
                            style={{ marginTop: 20 }}
                        >
                            Confirm
                        </Button>
                    </div>
                </ModalComponent>
            )}
        </div>
    );
};
